import { z } from 'zod'

export const LEARNING_LEARNER_LAUNCH_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_LAUNCH_SCHEMA_VERSION = 'learning-launch-v1' as const

const routeId = z.string().trim().min(1).max(180)
const slug = z.string().regex(/^[a-z0-9][a-z0-9-]{0,127}$/u)
const sha256 = z.string().trim().regex(/^(?:sha256:)?[a-f0-9]{64}$/u)

export const learnerLaunchRequestSchema = z.object({
  courseId: slug.optional(),
  moduleSlug: slug.optional(),
  programId: routeId.optional(),
  requirementId: routeId.optional(),
}).strict().superRefine((value, context) => {
  if (Boolean(value.programId) !== Boolean(value.requirementId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Program and requirement context must be supplied together.',
      path: ['programId'],
    })
  }
  if (!value.courseId && (value.moduleSlug || value.programId || value.requirementId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Course context is required when a launch target is supplied.',
      path: ['courseId'],
    })
  }
})

export const learnerLaunchTargetSchema = z.object({
  courseId: slug,
  moduleSlug: slug.optional(),
  artifactId: routeId,
  courseRevision: sha256,
  policyHash: sha256,
  programId: routeId.optional(),
  programVersionId: routeId.optional(),
  enrollmentId: routeId.optional(),
  requirementId: routeId.optional(),
}).strict()

const learnerLaunchBaseResponseSchema = z.object({
  schemaVersion: z.literal(LEARNING_LAUNCH_SCHEMA_VERSION),
})

export const learnerLaunchExistingSessionResponseSchema = learnerLaunchBaseResponseSchema.extend({
  mode: z.literal('existing-session'),
  target: learnerLaunchTargetSchema,
}).strict()

export const learnerLaunchExchangeResponseSchema = learnerLaunchBaseResponseSchema.extend({
  mode: z.literal('exchange'),
  target: learnerLaunchTargetSchema.optional(),
  assertion: z.string().min(1),
  provider: z.literal('vjal'),
  expiresAt: z.string().datetime({ offset: true }),
  outcome: z.enum(['created', 'reused', 'restored']).optional(),
  replayed: z.boolean().optional(),
}).strict()

export const learnerLaunchResponseSchema = z.discriminatedUnion('mode', [
  learnerLaunchExistingSessionResponseSchema,
  learnerLaunchExchangeResponseSchema,
])

export const learningLearnerLaunchRequestSchema = learnerLaunchRequestSchema
export const learningLearnerLaunchResponseSchema = learnerLaunchResponseSchema

export type LearnerLaunchRequest = z.infer<typeof learnerLaunchRequestSchema>
export type LearnerLaunchTarget = z.infer<typeof learnerLaunchTargetSchema>
export type LearnerLaunchExistingSessionResponse = z.infer<typeof learnerLaunchExistingSessionResponseSchema>
export type LearnerLaunchExchangeResponse = z.infer<typeof learnerLaunchExchangeResponseSchema>
export type LearnerLaunchResponse = z.infer<typeof learnerLaunchResponseSchema>
export type LearningLearnerLaunchRequest = z.infer<typeof learningLearnerLaunchRequestSchema>
export type LearningLearnerLaunchResponse = z.infer<typeof learningLearnerLaunchResponseSchema>

// Application-facing aliases used by the Mac learning worker contracts.
export const LearnerLaunchRequestSchema = learnerLaunchRequestSchema
export const LearnerLaunchResponseSchema = learnerLaunchResponseSchema
